import { useRef, useState, useCallback, useEffect, RefObject } from "react";

interface UseDragScrollOptions {
  /** Speed multiplier for drag scrolling (default: 1) */
  speed?: number;
  /** Threshold in pixels before drag starts (default: 5) */
  threshold?: number;
  /** Whether to disable scroll snap during drag (default: true) */
  disableSnapOnDrag?: boolean;
  /** Callback when drag starts */
  onDragStart?: () => void;
  /** Callback when drag ends */
  onDragEnd?: () => void;
}

interface UseDragScrollReturn {
  scrollRef: RefObject<HTMLDivElement>;
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
  handleMouseLeave: () => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: () => void;
  containerProps: {
    ref: RefObject<HTMLDivElement>;
    onMouseDown: (e: React.MouseEvent) => void;
    onMouseMove: (e: React.MouseEvent) => void;
    onMouseUp: () => void;
    onMouseLeave: () => void;
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
    style: React.CSSProperties;
    className: string;
  };
}

export const useDragScroll = (options: UseDragScrollOptions = {}): UseDragScrollReturn => {
  const { 
    speed = 1, 
    threshold = 5, 
    disableSnapOnDrag = true,
    onDragStart,
    onDragEnd 
  } = options;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const hasDraggedRef = useRef(false);
  const velocityRef = useRef(0);
  const lastMoveTimeRef = useRef(0);
  const lastXRef = useRef(0);
  const momentumAnimationRef = useRef<number>();

  // Momentum scrolling after release
  const applyMomentum = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const friction = 0.95;
    const minVelocity = 0.5;

    const animate = () => {
      if (Math.abs(velocityRef.current) > minVelocity) {
        container.scrollLeft -= velocityRef.current;
        velocityRef.current *= friction;
        momentumAnimationRef.current = requestAnimationFrame(animate);
      }
    };

    if (Math.abs(velocityRef.current) > minVelocity) {
      momentumAnimationRef.current = requestAnimationFrame(animate);
    }
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = scrollRef.current;
    if (!container) return;
    
    // Only start drag on left mouse button
    if (e.button !== 0) return;
    
    // Cancel any ongoing momentum
    if (momentumAnimationRef.current) {
      cancelAnimationFrame(momentumAnimationRef.current);
    }
    
    setIsDragging(true);
    hasDraggedRef.current = false;
    startXRef.current = e.pageX;
    scrollLeftRef.current = container.scrollLeft;
    lastXRef.current = e.pageX;
    lastMoveTimeRef.current = Date.now();
    velocityRef.current = 0;
    
    container.style.cursor = 'grabbing';
    container.style.userSelect = 'none';
    
    onDragStart?.();
  }, [onDragStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const container = scrollRef.current;
    if (!container) return;
    
    const deltaX = e.pageX - startXRef.current;
    
    // Only start actual scrolling after threshold
    if (Math.abs(deltaX) > threshold) {
      e.preventDefault();
      hasDraggedRef.current = true;
      container.scrollLeft = scrollLeftRef.current - (deltaX * speed);
      
      // Calculate velocity for momentum
      const now = Date.now();
      const dt = now - lastMoveTimeRef.current;
      if (dt > 0) {
        velocityRef.current = (e.pageX - lastXRef.current) / dt * 15;
      }
      lastXRef.current = e.pageX;
      lastMoveTimeRef.current = now;
    }
  }, [isDragging, threshold, speed]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    const container = scrollRef.current;
    if (container) {
      container.style.cursor = 'grab';
      container.style.userSelect = '';
    }
    
    // Apply momentum scrolling
    applyMomentum();
    
    onDragEnd?.();
  }, [isDragging, applyMomentum, onDragEnd]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      handleMouseUp();
    }
  }, [isDragging, handleMouseUp]);

  // Touch handlers for mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const container = scrollRef.current;
    if (!container) return;
    
    if (momentumAnimationRef.current) {
      cancelAnimationFrame(momentumAnimationRef.current);
    }
    
    setIsDragging(true);
    hasDraggedRef.current = false;
    startXRef.current = e.touches[0].pageX;
    scrollLeftRef.current = container.scrollLeft;
    lastXRef.current = e.touches[0].pageX;
    lastMoveTimeRef.current = Date.now();
    velocityRef.current = 0;
    
    onDragStart?.();
  }, [onDragStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const container = scrollRef.current;
    if (!container) return;
    
    const deltaX = e.touches[0].pageX - startXRef.current;
    
    if (Math.abs(deltaX) > threshold) {
      hasDraggedRef.current = true;
      container.scrollLeft = scrollLeftRef.current - (deltaX * speed);
      
      const now = Date.now();
      const dt = now - lastMoveTimeRef.current;
      if (dt > 0) {
        velocityRef.current = (e.touches[0].pageX - lastXRef.current) / dt * 15;
      }
      lastXRef.current = e.touches[0].pageX;
      lastMoveTimeRef.current = now;
    }
  }, [isDragging, threshold, speed]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    applyMomentum();
    onDragEnd?.();
  }, [isDragging, applyMomentum, onDragEnd]);

  // Prevent click events on links when dragging
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const preventClick = (e: MouseEvent) => {
      if (hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    container.addEventListener('click', preventClick, true);
    return () => container.removeEventListener('click', preventClick, true);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (momentumAnimationRef.current) {
        cancelAnimationFrame(momentumAnimationRef.current);
      }
    };
  }, []);

  // Convenience object for spreading onto container
  const containerProps = {
    ref: scrollRef,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseLeave,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    style: {
      scrollbarWidth: 'none' as const,
      msOverflowStyle: 'none' as const,
      WebkitOverflowScrolling: 'touch' as const,
      overscrollBehaviorX: 'contain' as const,
      cursor: isDragging ? 'grabbing' : 'grab',
      scrollSnapType: (disableSnapOnDrag && isDragging) ? 'none' : undefined,
    },
    className: isDragging ? 'select-none' : '',
  };

  return {
    scrollRef,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    containerProps,
  };
};
